import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Custom tailwind-merge configured for tw- prefix
 * Extended to recognize custom font-size classes (text-12, text-14, etc.)
 * so they don't conflict with text-color classes (text-white, text-primary, etc.)
 */
const twMerge = extendTailwindMerge({
  prefix: 'tw',
  extend: {
    theme: {
      color: [
        'primary',
        'primary-light',
        'primary-dark',
        'primary-darker',
        'error',
        'warn',
        'accent',
        'white',
        'black',
        'gray',
        'gray-light',
        'gray-lighter',
        'gray-dark',
        'gray-darker',
        'font',
        'logo-background',
        'whitelabel-background',
        'whitelabel-header',
        'pickup',
        'abm-navy',
        'abm-blue',
        'abm-orange',
        'abm-ink',
        'abm-orange-dark',
        'abm-blue-hover',
        'abm-orange-hover',
        'propark-charcoal',
        'propark-red',
        'propark-gold',
        'propark-mist',
        'propark-smoke',
        'propark-line',
        'propark-silver',
        'propark-blush',
        'propark-ink',
      ],
      spacing: [
        'nano',
        'micro',
        'small',
        'base',
        'large',
        'huge',
        'massive',
        'gargantuan',
        'colossal',
        'titanic',
        'monumental',
      ],
      'font-weight': ['regular', 'medium', 'semibold', 'bold'],
    },
    classGroups: {
      'font-size': [{ text: ['10', '12', '14', '16', '20', '24', '32', '40', '48'] }],
    },
  },
});

/**
 * Combines clsx and tailwind-merge for predictable class merging.
 * The last conflicting class always wins.
 *
 * @example
 * cn('tw-text-red-500', 'tw-text-blue-500') // → 'tw-text-blue-500'
 * cn('tw-p-4', condition && 'tw-p-2') // → 'tw-p-2' if condition is true
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Reusable Tailwind class constants for common patterns.
 * Use these as building blocks for component styles.
 */

// ===========================================
// BASE
// ===========================================

/** Base font for all components */
export const fontBase = 'tw:font-sans';

/** Centers content in an inline-flex or flex container */
export const inlineCenter = 'tw:flex tw:items-center tw:justify-center';

/** Standard focus ring for accessibility */
export const focusRing =
  'tw:focus:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-primary tw:focus-visible:ring-offset-2';

/** Base classes for interactive elements (buttons, inputs, etc.) */
export const interactiveBase =
  'tw:relative tw:inline-flex tw:items-center tw:justify-center tw:text-center tw:cursor-pointer tw:select-none tw:whitespace-nowrap';

/** Standard transitions */
export const transitionAll = 'tw:transition-all tw:duration-200';

/** Standard color transitions */
export const transitionColors = 'tw:transition-colors tw:duration-200';

// ===========================================
// STACKING ORDER
// ===========================================

/**
 * z-index for a dropdown/menu opened from inside a sheet or modal. Pass as `menuZIndex` to
 * `Select` / `MultiSelect` / `Autocomplete`, which forward it to `MenuList`.
 *
 * ## Invariants:
 * - Must exceed the BottomSheet bands (backdrop 51, panel 52) and the nested-sheet bands
 *   (60/70). Why: MenuList defaults to z-50, so a menu opened inside a sheet renders at the
 *   correct size and position and is then painted over by its own host — which reads as
 *   "the dropdown never opens" (PB-2256, PB-4561, then PB-2909). It is not a positioning
 *   bug; do not chase it as one.
 * - Must stay below the Toaster (100), the map (1000) and the cookie/captcha layers (9998+),
 *   which must remain able to cover an open menu.
 * - The number is meaningless on its own — it encodes the relationship above. Change it only
 *   after re-checking both neighbours.
 */
export const SHEET_MENU_Z_INDEX = 80;

// ===========================================
// SCAN-ENTRY SURFACES (/qr, /z, /t, /touchless)
// ===========================================

/**
 * Full-viewport shell for the scan-entry surfaces.
 *
 * ## Invariants:
 * - Below the `scan` breakpoint this must be `fixed inset-0` at z-30. Why: these routes live
 *   under the `(public)` route group, whose layout always mounts the app header (sticky, z-20)
 *   and the mobile tab bar around `{children}`. The chrome is never unmounted for a scan surface
 *   — it is painted over. A state that renders in normal flow therefore shows the header and tab
 *   bar that the loaded state hides, which is wrong for a parker who arrived by camera scan
 *   rather than by browsing the app.
 * - From the `scan` breakpoint up it returns to normal flow so the chrome is reachable again.
 * - The width itself is the `--breakpoint-scan` token (globals.css) and must NEVER be re-inlined
 *   as a literal here. Why, twice over: the app chrome switches on the same width — the layout
 *   header only moves to z-50 on the far side of it, so a mismatch leaves the surface
 *   half-covered — and the literal this replaced (440px) was the exact CSS width of an iPhone 16
 *   Pro Max, which `min-width` matched inclusively and handed a phone the desktop shell.
 *
 * Every branch of a scan surface — loading, resolving, redirecting, error, payment status —
 * uses this, not just the loaded one.
 */
export const scanSurfaceShell = [
  'tw:fixed tw:inset-0 tw:z-30 tw:flex tw:flex-col tw:bg-white',
  'tw:scan:relative tw:scan:inset-auto tw:scan:z-auto',
  'tw:scan:flex-1 tw:scan:min-h-0 tw:scan:overflow-hidden',
].join(' ');

/** `scanSurfaceShell` with its content centred — spinners, prompts and error messages. */
export const scanSurfaceCentered = `${scanSurfaceShell} tw:items-center tw:justify-center`;

/**
 * Top padding for the brand-coloured band that heads a scan surface — the QR hero
 * (`QrBrandHeader`) and the shared `BrandTopBar` band on the payment / touchless screens.
 *
 * ## Invariants:
 * - The status-bar allowance comes from `env(safe-area-inset-top)`, never from a fixed number.
 *   Why: the status bar is only INSIDE the viewport when the page runs standalone (installed PWA
 *   or in-app webview); in an ordinary browser tab the chrome sits above the viewport and the
 *   inset is 0. A flat allowance is therefore dead space on every browser visit — the band used
 *   a flat 64px (`pt-monumental`) and the hero a flat 24px, which is exactly what this replaces.
 * - The constant added on top is the band's own gutter, so it must stay small enough to be the
 *   whole padding in a browser. `micro` (8px) reads as a gutter; the previous 24px read as a gap.
 * - ONE definition, composed by both headers. Why: these are the same band by design, and each
 *   keeping its own copy is how the two drifted 56px apart in the first place — the drift
 *   `BrandTopBar.style.tsx` already carries an invariant warning about.
 */
export const scanHeaderTopClearance = [
  'tw:pt-[calc(env(safe-area-inset-top,0px)+var(--spacing-micro))]',
  // From the shell breakpoint up the app's own header sits above this band, so there is no
  // device chrome to clear — just a gutter.
  'tw:scan:pt-small',
].join(' ');

// ===========================================
// ENTER / EXIT TIME MARKERS
// ===========================================

/*
 * Single definition of the enter/exit time marker fill, shared by every surface
 * that shows an entry or exit time — search bar, garage detail, payment summary,
 * QR/zone booking, ticket timelines.
 *
 * Two rules hold here, and both exist because they were broken before:
 *
 * 1. The colour comes from configuration, never a literal. Enter follows the
 *    tenant's colorAccent and exit its colorBrandError (ThemeProvider maps both
 *    onto --tw-color-*). A hardcoded green ignores white-label tenants; one was
 *    already removed from the status badges under PB-1693, but the payment summary
 *    kept a stray #16a34a until this sweep.
 * 2. The gradient sheen is derived from that same colour by the dot-gradient
 *    utility (globals.css), so re-tinting the theme re-tints the gradient too.
 *
 * Compose with whatever size/shape the surface needs:
 *   cn('tw:w-3 tw:h-3 tw:rounded-full', enterMarkerFill)
 *
 * Adding a new enter/exit marker? Use these rather than writing colour classes —
 * enterExitMarkers.test.ts holds the whole set to this contract.
 */
export const enterMarkerFill =
  'tw:bg-accent tw:[--dot-gradient-color:var(--tw-color-accent)] tw:dot-gradient';

export const exitMarkerFill =
  'tw:bg-error tw:[--dot-gradient-color:var(--tw-color-error)] tw:dot-gradient';
